import type { HTMLAttributes } from 'react'
import React, { forwardRef } from 'react'
import { useWindowDimensions } from 'react-native'
import { twMerge } from 'tailwind-merge'

import * as Accordion from '@radix-ui/react-accordion'
import {
  accordionItem,
  accordionListContainer,
} from '@ui-kit/components/listComponents/AccordionList/styles'
import { BORDER_RADIUS, SCREEN_SIZE } from '@ui-kit/constants'
import { caption, h4 } from '@ui-kit/core/typography'
import { bgColor, borderColor } from '@ui-kit/styles/colors'
import { paddingX, paddingY } from '@ui-kit/styles/padding'

import type { AccordionContentProps, AccordionElementProps, AccordionRootProps } from './types'

const TriggerStateIconContainer = (props: HTMLAttributes<HTMLDivElement>) => {
  const { triggerActionIcon } = accordionItem({})
  return (
    <div
      {...props}
      className={triggerActionIcon({
        className: 'flex',
      })}
    />
  )
}

const Trigger = forwardRef<
  HTMLButtonElement,
  Accordion.AccordionTriggerProps & React.RefAttributes<HTMLButtonElement>
>((props, forwardedRef) => {
  const { trigger } = accordionItem({})
  return (
    <Accordion.Header>
      <Accordion.Trigger className={trigger()} ref={forwardedRef} {...props} />
    </Accordion.Header>
  )
})

const HeaderContainer = (props: HTMLAttributes<HTMLDivElement>) => {
  const { triggerTextContainer } = accordionItem({})
  return <div {...props} className={triggerTextContainer({})} />
}
const Title = (props: { children: React.ReactNode; className?: string }) => {
  return <div className={h4({ className: props.className })}>{props.children}</div>
}

const Subtitle = (props: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={caption({ emphasis: 'medium', className: props.className })}>
      {props.children}
    </div>
  )
}

const Root = (props: AccordionRootProps) => {
  const {
    fullContainerWidth = false,
    rowDividerBordered,
    borderColor: updatedBorderColor,
    style,
    ...rootProps
  } = props
  const borderedStyle = style === 'bordered'

  return (
    <Accordion.Root
      className={twMerge(
        borderColor({ color: updatedBorderColor }),
        accordionListContainer({
          bordered: borderedStyle && !rowDividerBordered,
          rowDividerBordered: rowDividerBordered,
        }),
        fullContainerWidth && 'w-full !max-w-full'
      )}
      {...rootProps}
    />
  )
}

const Item = (props: AccordionElementProps) => {
  const {
    backgroundColor,
    borderColor: updatedBorderColor,
    padding,
    rowDividerBordered,
    isExpandable,
    borderRadius,
    ...rest
  } = props

  const { container } = accordionItem({
    isExpandable: isExpandable,
    rowDividerBordered: rowDividerBordered,
  })

  const windowWidth = useWindowDimensions().width
  const space = windowWidth <= SCREEN_SIZE.M ? 'small' : 'gutter'

  const containerStyles = container()
  const mergedClassNames = twMerge(
    containerStyles,
    bgColor({ color: backgroundColor }),
    borderColor({ color: updatedBorderColor }),
    paddingY({ space: padding ?? space }),
    paddingX({ space: rowDividerBordered ? 'none' : padding ?? space }),
    updatedBorderColor
  )

  return (
    <Accordion.Item
      className={mergedClassNames}
      style={{
        borderRadius: borderRadius ? BORDER_RADIUS[borderRadius] : undefined,
      }}
      {...rest}
    />
  )
}

const Content = (props: AccordionContentProps) => {
  const { withIcon, ...rest } = props
  return (
    <Accordion.Content
      className={twMerge(
        'overflow-hidden pt-2.5 transition-all data-[state=closed]:animate-accordion-close data-[state=open]:animate-accordion-open',
        withIcon && 'ml-2.5 pl-6'
      )}
      {...rest}
    />
  )
}

const AccordionList = {
  Root,
  Item,
  Trigger,
  TriggerStateIconContainer,
  HeaderContainer,
  Title,
  Subtitle,
  Content,
}
export default AccordionList
